import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ProfileConfig } from '../pages/Profile/Profile.props';
import {
  currentUserQueryKey,
  useGetCurrentUser,
  useUpdateProfileConfig,
} from '../queries/user-query';

interface UsePinnedReportsReturn {
  /** Report codes pinned as tabs, in display order. */
  pinnedCodes: string[];
  /** Update just the pinned codes (pin / unpin / close); keeps the default index. */
  setPinnedCodes: (next: string[]) => void;
  /** Position of the (draggable) default tab among the pinned tabs. */
  defaultTabIndex: number;
  /** Commit a full reorder — new code order AND new default index — in one write. */
  reorderTabs: (codes: string[], index: number) => void;
}

/**
 * Persists a list page's tab layout — pinned report codes and the default tab's
 * position — into the current user's profile `config` blob, so they survive
 * reloads and follow the user across devices. Both pieces are written in a
 * single mutation so a reorder can't half-apply.
 *
 * @param pageName the report page key (e.g. "ServiceList"), scoping the layout.
 */
export const usePinnedReports = (pageName: string): UsePinnedReportsReturn => {
  const getCurrentUser = useGetCurrentUser();
  const userId = getCurrentUser.data?.id;
  const config = getCurrentUser.data?.config;
  const updateProfileConfig = useUpdateProfileConfig(userId ?? 0);
  const queryClient = useQueryClient();

  const persistedCodes = useMemo(() => config?.pinnedReports?.[pageName] ?? [], [config, pageName]);
  const persistedCodesKey = persistedCodes.join(' ');
  const persistedIndex = config?.reportTabDefaultIndex?.[pageName] ?? 0;

  const [pinnedCodes, setLocalCodes] = useState<string[]>(persistedCodes);
  const [defaultTabIndex, setLocalIndex] = useState<number>(persistedIndex);

  // Re-hydrate when the persisted config for this page changes (initial load, or
  // a save elsewhere). Keyed on stable value signals, not array/object identity.
  useEffect(() => {
    setLocalCodes(persistedCodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistedCodes is derived; persistedCodesKey is the stable change signal
  }, [persistedCodesKey]);

  useEffect(() => {
    setLocalIndex(persistedIndex);
  }, [persistedIndex]);

  const persist = useCallback(
    (codes: string[], index: number, rollback: () => void) => {
      if (!userId) return;
      // Merge against the FRESHEST cached config at save time, not the render
      // closure. This closes a lost-update window: a concurrent write to another
      // config key (myZones, another list page's tabs, or another browser tab)
      // that landed since this render would otherwise be clobbered by our stale
      // copy. getQueryData is a cheap synchronous cache read.
      const freshest: ProfileConfig =
        queryClient.getQueryData<{ config?: ProfileConfig }>(currentUserQueryKey())?.config ??
        config ??
        {};
      updateProfileConfig.mutate(
        {
          config: {
            ...freshest,
            pinnedReports: { ...(freshest.pinnedReports ?? {}), [pageName]: codes },
            reportTabDefaultIndex: { ...(freshest.reportTabDefaultIndex ?? {}), [pageName]: index },
          },
        },
        // Roll the optimistic local update back if the save fails, so the tabs
        // don't stay out of step with the (unchanged) server config.
        { onError: rollback }
      );
    },
    [userId, config, pageName, updateProfileConfig, queryClient]
  );

  const setPinnedCodes = useCallback(
    (next: string[]) => {
      const prevCodes = pinnedCodes;
      setLocalCodes(next);
      persist(next, defaultTabIndex, () => setLocalCodes(prevCodes));
    },
    [persist, pinnedCodes, defaultTabIndex]
  );

  const reorderTabs = useCallback(
    (codes: string[], index: number) => {
      const prevCodes = pinnedCodes;
      const prevIndex = defaultTabIndex;
      setLocalCodes(codes);
      setLocalIndex(index);
      persist(codes, index, () => {
        setLocalCodes(prevCodes);
        setLocalIndex(prevIndex);
      });
    },
    [persist, pinnedCodes, defaultTabIndex]
  );

  return { pinnedCodes, setPinnedCodes, defaultTabIndex, reorderTabs };
};
